import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type {
  BulkEditPayload,
  OpeningDays,
} from 'app/typings/core/extranet/rooms/roomBulkEdit';
import { apiMutation } from 'app/utils/request/api';

import { CORE_QUERY_KEYS as INVENTORY_CORE_QUERY_KEYS } from '../inventory';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export function useValidateRoomBulkEdit(hotelId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkEditPayload) => {
      return apiMutation(
        'POST',
        {
          path: ENDPOINTS.bulkValidateOpenings,
          params: { id: hotelId.toString(), roomId: payload.roomId.toString() },
        },
        payload
      ) as Promise<OpeningDays>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.bulkSaveOpenings, hotelId],
      });
    },
    onError: () => {
      message.error(`Error while validating bulk edit`);
    },
  });
}

export function useSaveRoomBulkEdit(
  hotelId: number,
  options: { startDate: string; endDate: string }
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BulkEditPayload) => {
      return apiMutation(
        'POST',
        {
          path: ENDPOINTS.bulkSaveOpenings,
          params: { id: hotelId.toString(), roomId: payload.roomId.toString() },
        },
        payload
      ) as Promise<void>;
    },
    onSuccess: () => {
      message.success(`Bulk edit openings saved successfully`);
      queryClient.invalidateQueries({
        queryKey: [CORE_QUERY_KEYS.bulkSaveOpenings, hotelId],
      });
      queryClient.invalidateQueries({
        queryKey: [INVENTORY_CORE_QUERY_KEYS.packScore, { hotelId }],
      });

      queryClient.invalidateQueries({
        queryKey: [
          INVENTORY_CORE_QUERY_KEYS.inventory,
          { hotelId, ...options },
        ],
      });
    },
    onError: () => {
      message.error(`Error while saving bulk edit openings`);
    },
  });
}
